/**
 * dashboard-page.tsx — Màn Tổng quan.
 *
 * Bố cục: lời chào → THANH LỌC TOÀN TRANG (kênh · tài khoản · kỳ) → 6 thẻ KPI
 * → khối báo cáo "Hiệu quả Chat → Đơn hàng".
 *
 * Vì sao bộ lọc nằm ở đây chứ không nằm trong khối báo cáo: trước đây kỳ và
 * phạm vi là trạng thái riêng của <ChatOrderReport>, còn sáu thẻ KPI gọi API
 * không tham số nên luôn hiện "hôm nay / 7 ngày" của TOÀN công ty. Người dùng
 * chọn tháng trước, hoặc chọn một tài khoản Zalo, thì phần trên vẫn đứng yên —
 * cùng một màn hình mà hai mốc thời gian, hai phạm vi. Nay chỉ có MỘT chỗ giữ
 * `mode` + `scope`, truyền xuống cả hai khối, và backend giải kỳ bằng chung
 * một hàm.
 *
 * Ba thẻ ghi "hiện tại" cố ý KHÔNG theo kỳ lọc: chưa trả lời, chưa đọc, tổng
 * khách hàng là ảnh chụp trạng thái lúc xem. Bảng dữ liệu chỉ lưu trạng thái
 * hiện thời, không lưu lịch sử trạng thái, nên "tháng trước còn bao nhiêu hội
 * thoại chưa trả lời" là câu hỏi không có đáp án đúng — hiện số hôm nay mà gắn
 * nhãn tháng trước thì còn sai hơn.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  MailOpen, MessageSquare, MessageSquareWarning, RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/shared/page-header'
import { StatCard } from '@/components/shared/stat-card'
import { formatNumber } from '@/lib/utils'
import { Loading } from '@/components/shared/feedback'
import { useAuthStore } from '@/stores/auth-store'
import { useDashboardKpi, type ChatReportMode } from '@/hooks/use-chat-report'
import { ChatOrderReport, PeriodFilter, TaiKhoanFilter } from './chat-order-report'
import { ALL_SCOPE, KenhTabs, docKenhDaLuu, luuKenh, type KenhBaoCao } from './kenh-tabs'
import { TraceDialog } from './trace-dialog'
import type { ChiSoTruyVet } from '@/hooks/use-drilldown'

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()

  const [mode, setMode] = useState<ChatReportMode>({ kind: 'period', period: 'month' })
  // Kênh là trục chính của màn: mỗi kênh một bộ chỉ số. Nhớ tab đã chọn cho lần mở sau.
  const [kenh, setKenh] = useState<KenhBaoCao>(() => docKenhDaLuu())
  const [accountId, setAccountId] = useState<string>(ALL_SCOPE)
  const chonKenh = (k: KenhBaoCao) => { setKenh(k); setAccountId(ALL_SCOPE); luuKenh(k) }
  const scope = {
    channel: kenh,
    accountId: accountId === ALL_SCOPE ? undefined : accountId,
  }

  const { data, isLoading } = useDashboardKpi(mode, scope)

  // Một hộp thoại truy vết dùng chung cho cả trang — mọi thẻ số đều mở nó.
  const [truyVet, setTruyVet] = useState<ChiSoTruyVet | null>(null)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['dashboard-kpi'] })
    qc.invalidateQueries({ queryKey: ['chat-report'] })
    qc.invalidateQueries({ queryKey: ['dashboard-message-volume'] })
    toast.success('Đã làm mới số liệu')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title={`Xin chào, ${user?.fullName || ''} 👋`}
          description="Chọn tab kênh ở trên cùng — mỗi kênh có bộ chỉ số riêng; rồi chọn tài khoản và kỳ. Bấm vào bất kỳ con số nào để xem nó lấy từ đâu."
        />
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="h-4 w-4" />
          Làm mới
        </Button>
      </div>

      {/* ★ Thanh lọc toàn trang — ghim trên cùng để luôn thấy đang xem kỳ nào */}
      <div className="sticky top-0 z-20 -mx-6 space-y-2 border-b bg-background/95 px-6 py-3 backdrop-blur">
        {/* Hàng 1: KÊNH — trục chính của báo cáo */}
        <KenhTabs kenh={kenh} onChange={chonKenh} />
        {/* Hàng 2: tài khoản trong kênh + kỳ */}
        <div className="flex flex-wrap items-center gap-2">
          <TaiKhoanFilter channel={kenh} accountId={accountId} onChange={setAccountId} />
          <PeriodFilter mode={mode} onChange={setMode} />
          <span className="ml-auto text-xs text-muted-foreground">
            Kỳ đang xem: <span className="font-semibold text-foreground">{data?.periodLabel ?? '…'}</span>
          </span>
        </div>
      </div>

      {isLoading || !data ? (
        <Loading />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label="Hội thoại"
            onClick={() => setTruyVet('conversations')}
            value={data.conversationsInRange}
            icon={MessageSquare}
            hint={data.periodLabel}
            giaiThich="Số hội thoại có tin nhắn trong kỳ (khách gửi hoặc mình gửi), mỗi hội thoại tính 1 lần dù trao đổi bao nhiêu tin."
          />
          <StatCard
            label="Hội thoại chưa trả lời"
            onClick={() => setTruyVet('unrepliedConversations')}
            value={data.unrepliedConversations}
            icon={MessageSquareWarning}
            tone="warning"
            hint="Hiện tại — cần xử lý"
          />
          <StatCard
            label="Hội thoại chưa đọc"
            onClick={() => setTruyVet('unreadConversations')}
            value={data.unreadConversations}
            icon={MailOpen}
            hint={`Hiện tại — ${formatNumber(data.unreadMessages)} tin chưa đọc`}
            giaiThich="Hội thoại còn ít nhất một tin chưa đọc, mỗi hội thoại tính 1 lần."
          />
        </div>
      )}

      {/* ★ Khối báo cáo chính: Hiệu quả Chat → Đơn hàng */}
      <ChatOrderReport mode={mode} scope={scope} onTruyVet={setTruyVet} kenh={kenh} />

      <TraceDialog metric={truyVet} mode={mode} scope={scope} onClose={() => setTruyVet(null)} />
    </div>
  )
}
